import FieldDestinationProvider from "../builder/common/fields/FieldDestinationProvider.tsx";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext.ts";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import useRequest from "@entrilia/shared/hooks/useRequest.ts";
import biApi from "../../../../../../api/biApi.ts";
import type { ReportTemplate } from "../../../../../../api/types/svTypes.ts";
import { ReportType, Status } from "../../../../../../api/types/svTypes.ts";
import GlobalDndProvider from "../../../../../common/GlobalDndProvider.tsx";
import {
  getEditReportTemplateUrl,
  getOrganizationReportTemplatesUrl,
} from "../../../report-lists/reports/utilities/editReportUrl.ts";
import SwitchStatusDialog from "../../../report-lists/templates/SwitchStatusDialog.tsx";
import { useBiClientContext } from "../../contexts/ClientContext";
import useCurrentReportConfiguration from "../../hooks/useCurrentReportConfiguration";
import { selectCurrentReport } from "../../store/selectors/currentReportSelectors";
import { selectCompanies } from "../../store/selectors/reportTemplatesSelectors";
import type { AppDispatch } from "../../store/store";
import { setEditReport, updateReport } from "../../store/thunks/currentReportThunks";
import { createCurrentReportAction } from "../../store/utils/storeUtils";
import { isChartReportType } from "../../utilities/reportTypeHelper.ts";
import ChartEditor from "../builder/chart/ChartEditor.tsx";
import CustomDragLayerInternal from "../builder/common/dragLayer/CustomDragLayer.tsx";
import FieldsComponent from "../builder/common/fields/Fields.tsx";
import { DeveloperPanelWrapper } from "../builder/dev/DeveloperPanelWrapper.tsx";
import PivotEditor from "../builder/pivot/PivotEditor.tsx";
import TabularEditor from "../builder/tabular/TabularEditor.tsx";
import CollapseContainer from "../common/CollapseContainer.tsx";
import EntityLevelAccess from "../common/EntityLevelAccess.tsx";
import PartContainer from "../viewer/common/PartContainer.tsx";
import MoreAction from "./MoreAction.tsx";
import SaveTemplate from "./SaveTemplate.tsx";
import SwitchStatusButton from "./SwitchStatusButton.tsx";
import TemplateActionHeader from "./TemplateActionHeader.tsx";
import TemplateEditor from "./TemplateEditor.tsx";
import TemplateNotFound from "./TemplateNotFound.tsx";

export default function EditTemplatePage() {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [showConfirmStatusChange, setShowConfirmStatusChange] = useState(false);
  const template = useSelector(selectCurrentReport) as ReportTemplate;
  const companies = useSelector(selectCompanies);
  const { clientInfo } = useBiClientContext();
  const { hasAnyPermission } = useClientPermissionsContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { getCurrentReportConfigurationAsync } = useCurrentReportConfiguration();

  const chartTemplate = useMemo(() => isChartReportType(template.reportType), [template]);

  const statusRef = useRef<Status>(template?.status || Status.Draft);
  const switchStatus = useCallback(
    () =>
      biApi.updateReportTemplateStatus({
        templateId: template.reportId,
        status: statusRef.current,
      }),
    [template.reportId]
  );

  const organizationTemplatesUrl = useMemo(
    () => getOrganizationReportTemplatesUrl(clientInfo.organization),
    [clientInfo.organization]
  );

  const { request, isLoading: switching } = useRequest(switchStatus);

  const handleStatusChange = async () => {
    const [, error] = await request();
    setShowConfirmStatusChange(false);
    if (!error) {
      dispatch(updateReport({ status: statusRef.current }));
      sendNotification("The template status was successfully changed.");
      return;
    }
    sendNotificationError("An error occurred while changing the template status.");
  };

  useEffect(() => {
    dispatch(createCurrentReportAction("updateReportMode", true));
  }, [dispatch]);

  if (template === undefined) return <></>;

  if (!hasAnyPermission(["ManageOrganizationReportTemplates"])) {
    return <TemplateNotFound backUrl={organizationTemplatesUrl} />;
  }

  if (companies.length === 0)
    return <TemplateNotFound backUrl={getOrganizationReportTemplatesUrl(clientInfo.organization)} />;

  return (
    <GlobalDndProvider>
      <FieldDestinationProvider key={template.reportId}>
        <Stack sx={{ flex: 1 }}>
          <TemplateActionHeader template={template} clientTitle={clientInfo.title} companies={companies}>
            <Stack direction={"row"} sx={{ alignItems: "center", gap: 1 }}>
              <EntityLevelAccess />
              {template?.reportId && (
                <SwitchStatusButton
                  currentStatus={template.status}
                  disabled={switching}
                  onClick={() => setShowConfirmStatusChange(true)}
                />
              )}
              <SaveTemplate template={template} companies={companies} clientInfo={clientInfo} />
              {template.reportId !== undefined && (
                <MoreAction
                  template={template}
                  getTemplateConfigurationAsync={getCurrentReportConfigurationAsync}
                  companies={companies}
                  onEdited={(rt, dataSourceChanged) => {
                    dispatch(setEditReport(rt));
                    if (dataSourceChanged) {
                      navigate(
                        getEditReportTemplateUrl(
                          template.organization,
                          rt.dataSource || clientInfo.clientCode,
                          template.reportId
                        )
                      );
                    }
                  }}
                  onDataSourceChanged={(ds) =>
                    navigate(
                      getEditReportTemplateUrl(template.organization, ds || clientInfo.clientCode, template.reportId)
                    )
                  }
                />
              )}
            </Stack>
          </TemplateActionHeader>
          <Divider />
          <Box sx={{ display: "flex", flex: 1 }}>
            <CollapseContainer
              expandCollapsePaddingTop={25}
              collapseElement={
                <Typography
                  variant="h6"
                  sx={(theme) => ({ color: theme.palette.text.primary, transform: "rotate(90deg)" })}
                >
                  Fields
                </Typography>
              }
            >
              <PartContainer sx={{ maxWidth: "21rem", minWidth: "21rem" }} caption={<Box></Box>}>
                <FieldsComponent />
              </PartContainer>
            </CollapseContainer>
            {(!template.reportType || template.reportType === ReportType.Pivot) && (
              <TemplateEditor Editor={PivotEditor} template={template} />
            )}
            {template.reportType === ReportType.Tabular && (
              <TemplateEditor Editor={TabularEditor} template={template} />
            )}
            {chartTemplate && <TemplateEditor Editor={ChartEditor} template={template} />}
            <DeveloperPanelWrapper />
          </Box>
          {showConfirmStatusChange && (
            <SwitchStatusDialog
              templateName={template.name}
              currentStatus={template.status}
              onClose={() => setShowConfirmStatusChange(false)}
              onConfirm={(status) => {
                statusRef.current = status;
                handleStatusChange();
              }}
              loading={switching}
            />
          )}
        </Stack>
        <CustomDragLayerInternal />
      </FieldDestinationProvider>
    </GlobalDndProvider>
  );
}
